import { createMachine, assign, DoneInvokeEvent, spawn, ActorRef, send } from 'xstate';
import { pure } from 'xstate/lib/actions';
import i18n from 'i18next';
import { accSpeedTest } from '../../services/speedTest.service';
import { recorderMachineV2 } from './recorderMachineV2';
import { accUploaderMachine } from './accUploaderMachine';
import { networkMachine } from './networkMachine';
import { EVENTS as NETWORK_EVENTS } from '../../interfaces/networkInterface';
import { storageMachine } from './storageMachine';
import { TAKE_TYPES, EVENTS as STORAGE_EVENTS } from '../../interfaces/storageInterface';
import { InitialState, STATES, ACTIONS, EVENTS, GUARDS, TAGS, CAMERA_STATES, IChecksMetaObj, MICROPHONE_STATES, INTERNET_STATES, SERVICES, VIDEO_CORRUPTED_STATES } from '../../interfaces/widgetInterface';
import { previewMachine } from './previewMachine';
import { EVENTS as PREVIEW_EVENTS } from '../../interfaces/previewInterface';
import { createVideo, getVideo, updateVideo, updateVideoFile } from '../../services/video.service';
import { updateVideoAxiosAuth } from '../../services/axiosInstances.service';
import { IWidgetConfig } from '../../interfaces/configInterface';
import { STATES as RECORDER_STATES, EVENTS as RECORDER_EVENTS, SPEED_TEST_LEVEL } from '../../interfaces/recorderInterface';
import { DEFAULT_COUNT_DOWN, DEFAULT_DURATION, DEFAULT_VIDEO_DIMENSIONS, FAST_UPLOAD_SPEED, MAX_CONFIRM_ATTEMPTS, MAX_FAILED_RECORDING_ATTEMPTS, MICROPHONE_NO_SOUND_ERROR_CODE, SLOW_UPLOAD_SPEED } from '../../utils/constants.utils';
import { CAMERA_STATES_MESSAGES, INTERNET_STATES_MESSAGES, MICROPHONE_STATES_MESSAGES } from '../../utils/messages.utils';
import { STATUS_CODES } from '../../utils/statusCodes.utils';
import { SentryTrackingFn } from '../../utils/sentry.utils';
import { triggerEvent, updateSession } from '../../services/session.service';
import configFile from '../../config';
import { IVideo, ALL_VIDEO_FILE_TYPES } from '../../interfaces/videoInterface';
import { DEBUG } from '../../utils/debug.utils';
import { recorderErrors, TYPES_EXPLANATION } from '../../utils/recorderErrors.utils';
import { ANSWER_TYPES } from '../../interfaces/jobInterface';
import { counterMachine } from './counterMachine';
import { BANDWIDTH_CONNECTION, emitTrackEvent, IEventType, EVENT_TYPES, ITrackEvent, updateEventId } from '../../services/event.service';
import { getUAParserData } from '../../utils/device.utils';

const CONSOLE_LOG_STYLE = 'background:#06f; color:#fff; margin: 10px 0; padding:5px; border-radius:4px;';

const getUploaderName = (currentQuestion: number) => `uploader-${currentQuestion}`;

interface IEventTypeByMode {
  QUESTION: IEventType;
  PRACTICE: IEventType;
}

interface IMapedEventTypes {
  PRESENTED: IEventTypeByMode;
  STARTED: IEventTypeByMode;
  STOPPED: IEventTypeByMode;
  TIMES_UP: IEventTypeByMode;
}

const MAPPED_EVENT_TYPES: IMapedEventTypes = {
  PRESENTED: {
    QUESTION: EVENT_TYPES.QUESTION_PRESENTED,
    PRACTICE: EVENT_TYPES.PRACTICE_PRESENTED,
  },
  STARTED: {
    QUESTION: EVENT_TYPES.QUESTION_STARTED,
    PRACTICE: EVENT_TYPES.PRACTICE_STARTED,
  },
  STOPPED: {
    QUESTION: EVENT_TYPES.QUESTION_ENDED,
    PRACTICE: EVENT_TYPES.PRACTICE_ENDED,
  },
  TIMES_UP: {
    QUESTION: EVENT_TYPES.TIMES_UP,
    PRACTICE: EVENT_TYPES.TIMES_UP,
  },
};

const questionEventFormatter = (questionActionType: keyof typeof MAPPED_EVENT_TYPES) => pure<InitialState, DoneInvokeEvent<any>>(({ recordingType, questions, currentQuestion, widgetConfig, currentTake }, event) => {
  const isQuestionMode = recordingType === TAKE_TYPES.QUESTION;
  const currentQuestionFile = isQuestionMode ? widgetConfig?.video?.videos?.[currentQuestion - 1] : null;
  const currentQuestionObj = questions[currentQuestion - 1];
  const isAssessment = currentQuestionObj.answerType && currentQuestionObj.answerType !== ANSWER_TYPES.VIDEO;
  return {
    type: ACTIONS.EMIT_TRACKING_EVENT,
    data: {
      eventType: isQuestionMode ? MAPPED_EVENT_TYPES[questionActionType].QUESTION : MAPPED_EVENT_TYPES[questionActionType].PRACTICE,
      ...(currentQuestionFile && { extraData: {
        questionFilename: currentQuestionFile.filename,
        questionNumber: currentQuestion,
        ...(!isAssessment && {
          currentTake,
          numberOfTakes: currentQuestionObj.numOfRetakes,
          isActivelyStopped: (event.data || {}).isActivelyStopped,
        }),
        questionType: currentQuestionObj.videoQuestion ? 'video' : 'text',
        answerType: currentQuestionObj.answerType || ANSWER_TYPES.VIDEO,
      } }),
    } as ITrackEvent,
  };
});

export const accWidgetMachine = createMachine<InitialState, DoneInvokeEvent<any>>(
  {
    predictableActionArguments: true,
    id: 'widget',
    initial: STATES.INIT,
    context: {
      isConnected: false,
      widgetConfig: {
        candidate: {
          username: '',
          email: '',
        },
        config: {
          auth: '',
        },
      },
      questions: [],
      speedTestResult: 0,
      networkRef: null,
      recorderRef: null,
      storageRef: null,
      previewRef: null,
      uploaderRefs: {},
      videoRef: { current: null },
      countDownRef: null,
      timer: 0,
      currentQuestion: 1,
      currentTake: 1,
      selectedTake: null,
      recordingType: TAKE_TYPES.PRACTICE,
      totalFileSize: 0,
      totalUploadedFilesSize: {},
      totalUploadSpeed: {},
      checksState: {
        camera: 'WAITING',
        microphone: 'WAITING',
        internet: 'DISCONNECTED',
      },
      checksMessage: {
        camera: CAMERA_STATES_MESSAGES.WAITING,
        microphone: MICROPHONE_STATES_MESSAGES.WAITING,
        internet: INTERNET_STATES_MESSAGES.DISCONNECTED,
      },
      error: null,
      failedRecordingAttempts: 0,
      failedRecordingMessage: VIDEO_CORRUPTED_STATES.NO_ERROR,
      isResumed: false,
      videoDimensions: DEFAULT_VIDEO_DIMENSIONS,
      confirmUploadedFalseCount: 0,
    },
    on: {
      [NETWORK_EVENTS.CONNECTION_CHANGED]: {
        actions: [
          ACTIONS.SET_CONNECTION,
          { type: ACTIONS.EMIT_TRACKING_EVENT, data: { eventType: EVENT_TYPES.RECONNECTED } as ITrackEvent },
        ],
      },
      [EVENTS.UPLOADER_PROGRESS]: {
        actions: [ACTIONS.UPDATE_TOTAL_UPLOADED_FILES_SIZE],
      },
      [EVENTS.UPLOADER_FINISHED]: {
        actions: [ACTIONS.UPDATE_TOTAL_UPLOADED, ACTIONS.REVOKE_MEMORY],
      },
      [EVENTS.UPDATE_VIDEO_DIMENSIONS]: {
        actions: [ACTIONS.UPDATE_VIDEO_DIMENSIONS],
      },
    },
    entry: [
      ACTIONS.UPDATE_AXION_AUTH,
      ACTIONS.SPAWN_NETWORK,
    ],
    states: {
      [STATES.INIT]: {
        entry: [{ type: ACTIONS.CONSOLE_DEBUG, data: { message: DEBUG.FETCHING_INTERVIEW } }],
        tags: [TAGS.LOADING],
        invoke: {
          id: 'createVideo',
          src: ({ widgetConfig: { candidate, job, video, config } }) => createVideo({ candidate, job, video: ({ ...video, platform: getUAParserData(navigator.userAgent) }) }, config.event_id),
          onDone: [
            {
              actions: [ACTIONS.SET_CONFIG, ACTIONS.SET_EVENT_ID, ACTIONS.SET_SESSION, { type: ACTIONS.CONSOLE_DEBUG, data: { message: DEBUG.INTERVIEW_DATA_RECEIVED } }],
              target: STATES.WELCOME,
              cond: GUARDS.SHOULD_SHOW_WELCOME,
            },
            {
              actions: [ACTIONS.SET_CONFIG, ACTIONS.SET_EVENT_ID, ACTIONS.SET_SESSION, { type: ACTIONS.CONSOLE_DEBUG, data: { message: DEBUG.INTERVIEW_DATA_RECEIVED } }],
              target: STATES.SETUP,
            },
          ],
          onError: {
            actions: [
              ACTIONS.SET_VIDEO_ERROR,
              ACTIONS.SENTRY,
              { type: ACTIONS.CONSOLE_DEBUG, data: { message: DEBUG.FETCHING_INTERVIEW_ERROR } },
            ],
            target: STATES.ERROR,
          },
        },
      },
      [STATES.WELCOME]: {
        entry: [{ type: ACTIONS.CONSOLE_DEBUG, data: { message: DEBUG.WELCOME_PAGE_STAGE } }],
        on: {
          [EVENTS.NEXT_STEP]: {
            actions: [{ type: ACTIONS.EMIT_TRACKING_EVENT, data: { eventType: EVENT_TYPES.WELCOME_STATE_START_BUTTON } as ITrackEvent }],
            target: STATES.SETUP,
          },
        },
      },
      [STATES.SETUP]: {
        initial: STATES.INIT,
        entry: [{ type: ACTIONS.CONSOLE_DEBUG, data: { message: DEBUG.SETUP_STAGE } }],
        tags: [TAGS.SHOW_CAMERA, TAGS.DISPLAY_OUTER_VIEW],
        states: {
          [STATES.INIT]: {
            always: [
              { target: `#widget.${STATES.CONFIRM}`, cond: GUARDS.ARE_ALL_QUESTIONS_UPLOADED },
              { target: STATES.SETUP__INIT_STORAGE, cond: GUARDS.NO_STORAGE },
              { target: STATES.SETUP__INIT_RECORDER, cond: GUARDS.NO_RECORDER },
              {
                target: `#widget.${STATES.NEXT_QUESTION_MIDDLEWARE}`,
                cond: GUARDS.THERE_ARE_NO_VIDEO_QUESTIONS_TYPE,
                actions: [
                  ACTIONS.SET_QUESTION_MODE,
                  ACTIONS.SET_NEXT_QUESTION,
                  { type: ACTIONS.CONSOLE_DEBUG, data: { message: DEBUG.INTERVIEW_STARTED } },
                  { type: ACTIONS.EMIT_TRACKING_EVENT, data: { eventType: EVENT_TYPES.START_CLICKED } as ITrackEvent },
                ],
              },
              { target: STATES.SETUP__TEST, actions: [ACTIONS.RECORDER_RE_INIT] },
            ],
          },
          [STATES.SETUP__INIT_STORAGE]: {
            entry: [ACTIONS.SPAWN_STORAGE],
            always: { target: STATES.INIT },
          },
          [STATES.SETUP__INIT_RECORDER]: {
            entry: [ACTIONS.SPAWN_RECORDER],
            always: { target: STATES.INIT },
          },
          [STATES.SETUP__TEST]: {
            type: 'parallel',
            on: {
              [EVENTS.PRACTICE_MODE]: {
                actions: [
                  ACTIONS.SET_PRACTICE_MODE,
                  { type: ACTIONS.SESSION_EVENT, data: { event: 'widget_practiced', type: 'date' } },
                  { type: ACTIONS.EMIT_TRACKING_EVENT, data: { eventType: EVENT_TYPES.PRACTICE_CLICKED as IEventType } },
                  { type: ACTIONS.CONSOLE_DEBUG, data: { message: DEBUG.PRACTICE_STARTED } },
                ],
                target: `#widget.${STATES.NEXT_QUESTION_MIDDLEWARE}`,
                cond: GUARDS.CAN_START_INTERVIEW,
              },
              [EVENTS.QUESTION_MODE]: {
                actions: [
                  ACTIONS.SET_QUESTION_MODE,
                  ACTIONS.SET_NEXT_QUESTION,
                  { type: ACTIONS.CONSOLE_DEBUG, data: { message: DEBUG.INTERVIEW_STARTED } },
                  { type: ACTIONS.EMIT_TRACKING_EVENT, data: { eventType: EVENT_TYPES.START_CLICKED } as ITrackEvent },
                ],
                target: `#widget.${STATES.NEXT_QUESTION_MIDDLEWARE}`,
                cond: GUARDS.CAN_START_INTERVIEW,
              },
            },
            states: {
              [STATES.SETUP__TEST__CAMERA]: {
                initial: STATES.SETUP__TEST__CAMERA__WAITING,
                on: {
                  [EVENTS.RECORDER_ERROR]: {
                    actions: [{ type: ACTIONS.CONSOLE_DEBUG, data: { message: `CAMERA: ${DEBUG.MEDIA_DEVICE_ERROR}` } }],
                    target: `.${STATES.SETUP__TEST__CAMERA__ERROR}`,
                    cond: GUARDS.IS_VIDEO_ERROR,
                  },
                  [EVENTS.READY_TO_START_RECORDING]: [
                    {
                      actions: [{ type: ACTIONS.CONSOLE_DEBUG, data: { message: `CAMERA: ${DEBUG.MEDIA_DEVICE_SWITCH_OFF}` } }],
                      target: `.${STATES.SETUP__TEST__CAMERA__SKIP}`,
                      cond: GUARDS.IS_VIDEO_RECORDING_SKIP,
                    },
                    {
                      actions: [{ type: ACTIONS.CONSOLE_DEBUG, data: { message: `CAMERA: ${DEBUG.MEIDA_DEVICE_SUCCESS}` } }],
                      target: `.${STATES.SETUP__TEST__CAMERA__SUCCESS}`,
                    },
                  ],
                },
                states: {
                  [STATES.SETUP__TEST__CAMERA__WAITING]: {
                    entry: [{ type: ACTIONS.SET_CHECKS, data: { key: 'camera', state: CAMERA_STATES.WAITING, message: CAMERA_STATES_MESSAGES.WAITING } as IChecksMetaObj }],
                  },
                  [STATES.SETUP__TEST__CAMERA__ERROR]: {
                    entry: [{ type: ACTIONS.SET_CHECKS, data: { key: 'camera', state: CAMERA_STATES.ERROR } as IChecksMetaObj }],
                  },
                  [STATES.SETUP__TEST__CAMERA__SUCCESS]: {
                    entry: [{ type: ACTIONS.SET_CHECKS, data: { key: 'camera', state: CAMERA_STATES.READY, message: CAMERA_STATES_MESSAGES.READY } as IChecksMetaObj }],
                  },
                  [STATES.SETUP__TEST__CAMERA__SKIP]: {
                    entry: [{ type: ACTIONS.SET_CHECKS, data: { key: 'camera', state: CAMERA_STATES.SKIP, message: CAMERA_STATES_MESSAGES.READY } as IChecksMetaObj }],
                  },
                },
              },
              [STATES.SETUP__TEST__MICROPHONE]: {
                initial: STATES.SETUP__TEST__MICROPHONE__WAITING,
                on: {
                  [EVENTS.RECORDER_ERROR]: {
                    actions: [{ type: ACTIONS.CONSOLE_DEBUG, data: { message: `MICROPHONE: ${DEBUG.MEDIA_DEVICE_ERROR}` } }],
                    target: `.${STATES.SETUP__TEST__MICROPHONE__ERROR}`,
                    cond: GUARDS.IS_AUDIO_ERROR,
                  },
                  [EVENTS.READY_TO_START_RECORDING]: {
                    actions: [{ type: ACTIONS.CONSOLE_DEBUG, data: { message: `MICROPHONE: ${DEBUG.MEIDA_DEVICE_SUCCESS}` } }],
                    target: `.${STATES.SETUP__TEST__MICROPHONE__SUCCESS}`,
                  },
                },
                states: {
                  [STATES.SETUP__TEST__MICROPHONE__WAITING]: {
                    entry: [{ type: ACTIONS.SET_CHECKS, data: { key: 'microphone', state: MICROPHONE_STATES.WAITING, message: MICROPHONE_STATES_MESSAGES.WAITING } as IChecksMetaObj }],
                  },
                  [STATES.SETUP__TEST__MICROPHONE__ERROR]: {
                    entry: [{ type: ACTIONS.SET_CHECKS, data: { key: 'microphone', state: MICROPHONE_STATES.ERROR } as IChecksMetaObj }],
                  },
                  [STATES.SETUP__TEST__MICROPHONE__SUCCESS]: {
                    entry: [{ type: ACTIONS.SET_CHECKS, data: { key: 'microphone', state: MICROPHONE_STATES.READY, message: MICROPHONE_STATES_MESSAGES.READY } as IChecksMetaObj }],
                  },
                },
              },
              [STATES.SETUP__TEST__INTERNET]: {
                initial: STATES.SETUP__TEST__INTERNET__IDLE,
                states: {
                  [STATES.SETUP__TEST__INTERNET__IDLE]: {
                    entry: [{ type: ACTIONS.SET_CHECKS, data: { key: 'internet', state: INTERNET_STATES.DISCONNECTED, message: INTERNET_STATES_MESSAGES.DISCONNECTED } as IChecksMetaObj }],
                    always: {
                      target: STATES.SETUP__TEST__INTERNET__TESTING,
                      cond: GUARDS.IS_CONNECTED,
                    },
                  },
                  [STATES.SETUP__TEST__INTERNET__TESTING]: {
                    entry: [
                      ACTIONS.SET_SPEED_TEST,
                      { type: ACTIONS.SET_CHECKS, data: { key: 'internet', state: INTERNET_STATES.TESTING, message: INTERNET_STATES_MESSAGES.TESTING } as IChecksMetaObj },
                    ],
                    invoke: {
                      id: 'accSpeedTest',
                      src: accSpeedTest,
                      onDone: {
                        actions: [
                          ACTIONS.SET_SPEED_TEST,
                          ACTIONS.UPDATE_RECORDER_SPEED_TEST,
                          (_, event) => ({
                            type: ACTIONS.SESSION_EVENT,
                            data: {
                              event: event.data > FAST_UPLOAD_SPEED
                                ? 'widget_high_internet_setup' : event.data > SLOW_UPLOAD_SPEED ? 'widget_medium_internet_setup' : 'widget_slow_internet_setup',
                              type: 'date',
                            },
                          }),
                          pure((_, event) => ({
                            type: ACTIONS.EMIT_TRACKING_EVENT,
                            data: {
                              eventType: EVENT_TYPES.BANDWIDTH_CHECK as IEventType,
                              extraData: {
                                bandwidthSpeed: event.data,
                                bandwidthLevel: event.data >= FAST_UPLOAD_SPEED ? BANDWIDTH_CONNECTION.GOOD : event.data > SLOW_UPLOAD_SPEED ? BANDWIDTH_CONNECTION.SLOW : BANDWIDTH_CONNECTION.VERY_SLOW,
                              },
                            },
                          })),
                          { type: ACTIONS.CONSOLE_DEBUG, data: { message: DEBUG.SPEED_TEST_DONE } },
                        ],
                        target: STATES.SETUP__TEST__INTERNET__FINISHED,
                      },
                      onError: {
                        actions: [ACTIONS.SENTRY],
                        target: STATES.SETUP__TEST__INTERNET__IDLE,
                      },
                    },
                  },
                  [STATES.SETUP__TEST__INTERNET__FINISHED]: {
                    entry: [{ type: ACTIONS.SET_CHECKS, data: { key: 'internet' } as IChecksMetaObj }],
                    always: {
                      target: STATES.SETUP__TEST__INTERNET__IDLE,
                      actions: [{ type: ACTIONS.SESSION_EVENT, data: { event: 'NO_INTERNET', type: 'warning' } }],
                      cond: GUARDS.IS_DISCONNECTED,
                    },
                    on: {
                      [EVENTS.RETRY]: {
                        target: STATES.SETUP__TEST__INTERNET__TESTING,
                        cond: GUARDS.CAN_RETEST_SPEED_CONNECTION,
                      },
                    },
                  },
                },
              },
            },
          },
        },
      },
      [STATES.INTERVIEW]: {
        initial: STATES.INTERVIEW__INIT,
        entry: [send({ type: RECORDER_EVENTS.SET_MIC_SPEED_DETECTION }, { to: (context) => context.recorderRef as ActorRef<DoneInvokeEvent<any>> }),
          ACTIONS.UPDATE_RECORDER_PARAMS, ACTIONS.UPDATE_VIDEO_OBJECT, ACTIONS.UPDATE_VIDEO_PART, questionEventFormatter('PRESENTED')],
        exit: [
          questionEventFormatter('STOPPED'),
        ],
        on: {
          [EVENTS.RECORDER_ERROR]: {
            actions: [
              { type: ACTIONS.SENTRY, data: { eventName: recorderErrors.NoSoundError.name } },
              { type: ACTIONS.SET_CHECKS, data: { key: 'microphone', state: MICROPHONE_STATES.ERROR } as IChecksMetaObj },
            ],
          },
          [EVENTS.READY_TO_START_RECORDING]: {
            actions: [{ type: ACTIONS.SET_CHECKS, data: { key: 'microphone', state: MICROPHONE_STATES.READY, message: MICROPHONE_STATES_MESSAGES.READY } as IChecksMetaObj }],
          },
        },
        states: {
          [STATES.INTERVIEW__INIT]: {
            invoke: [
              {
                id: 'updateVideoToStartedTrue',
                src: SERVICES.UPDATE_VIDEO_OBJECT_CALL,
                data: { force: true },
                onDone: {
                  actions: [],
                },
                onError: {
                  actions: [() => console.error('Video update failed: [video started]'), ACTIONS.SENTRY],
                },
              },
              {
                id: 'updateVideoPartToStartedTrue',
                src: SERVICES.UPDATE_VIDEO_PART_CALL,
                onDone: {
                  actions: [],
                },
                onError: {
                  actions: [() => console.error('Video part update failed: [video part started]'), ACTIONS.SENTRY],
                },
              },
            ],
            always: [
              { target: STATES.INTERVIEW__EXPLANATION, cond: GUARDS.IS_THINKING_TIME },
              { target: STATES.INTERVIEW__EXPLANATION, cond: GUARDS.IS_VIDEO_QUESTION },
              { target: STATES.INTERVIEW__QUESTION },
            ],
          },
          [STATES.INTERVIEW__EXPLANATION]: {
            tags: [TAGS.SHOW_CAMERA, TAGS.DISPLAY_QUESTIONS_LIST],
            on: {
              [EVENTS.NEXT_STEP]: [
                { target: STATES.INTERVIEW__VIDEO_QUESTION, cond: GUARDS.IS_VIDEO_QUESTION },
                { target: STATES.INTERVIEW__QUESTION },
              ],
            },
          },
          [STATES.INTERVIEW__VIDEO_QUESTION]: {
            tags: [TAGS.CAN_START_RECORDING, TAGS.DISPLAY_QUESTION, TAGS.DISPLAY_OUTER_VIEW],
            on: {
              [EVENTS.NEXT_STEP]: {
                target: STATES.INTERVIEW__ANSWER_SELECTOR,
              },
            },
          },
          [STATES.INTERVIEW__QUESTION]: {
            tags: [TAGS.SHOW_CAMERA, TAGS.CAN_START_RECORDING, TAGS.DISPLAY_OUTER_VIEW, TAGS.DISPLAY_QUESTION, TAGS.DISPLAY_QUESTIONS_LIST],
            always: [
              { target: STATES.INTERVIEW__ANSWER_SELECTOR, cond: GUARDS.IS_THINKING_TIME },
            ],
            on: {
              [EVENTS.NEXT_STEP]: [
                { target: STATES.INTERVIEW__ANSWER_SELECTOR,
                  actions: [{ type: ACTIONS.SESSION_EVENT, data: { event: 'widget_start_click_setup', type: 'date' } }],
                },
              ],
            },
          },
          [STATES.INTERVIEW__ANSWER_SELECTOR]: {
            always: [
              { target: STATES.INTERVIEW__ASSESSMENT, cond: GUARDS.IS_ASSESSMENT_QUESTION },
              { target: STATES.INTERVIEW__RECORDING },
            ],
          },
          [STATES.INTERVIEW__ASSESSMENT]: {
            tags: [TAGS.DISPLAY_QUESTION],
            entry: [
              { type: ACTIONS.SPAWN_COUNT_DOWN, data: { callback: EVENTS.UPDATE_TIMER } },
              questionEventFormatter('STARTED'),
            ],
            exit: [ACTIONS.STOP_COUNT_DOWN],
            on: {
              [EVENTS.UPDATE_TIMER]: {
                actions: [ACTIONS.UPDATE_TIMER],
              },
              [EVENTS.NEXT_QUESTION]: [
                {
                  actions: [
                    ACTIONS.UPDATE_VIDEO_FILE_COMPLETED,
                    ACTIONS.SPAWN_UPLOADER,
                    ACTIONS.UPDATE_VIDEO_OBJECT,
                  ],
                  cond: GUARDS.IS_LAST_INTERVIEW_QUESTION,
                  target: `#widget.${STATES.UPLOADING}`,
                },
                {
                  actions: [
                    questionEventFormatter('TIMES_UP'),
                    ACTIONS.UPDATE_VIDEO_FILE_COMPLETED,
                    ACTIONS.SPAWN_UPLOADER,
                    ACTIONS.UPDATE_QUESTION_NUMBER,
                  ],
                  cond: GUARDS.IS_TIMES_UP,
                  target: `#widget.${STATES.TIMES_UP}`,
                },
                {
                  actions: [
                    ACTIONS.UPDATE_VIDEO_FILE_COMPLETED,
                    ACTIONS.SPAWN_UPLOADER,
                    ACTIONS.UPDATE_QUESTION_NUMBER,
                  ],
                  target: `#widget.${STATES.NEXT_QUESTION_MIDDLEWARE}`,
                },
              ],
            },
          },
          [STATES.INTERVIEW__RECORDING]: {
            tags: [TAGS.SHOW_CAMERA, TAGS.DISPLAY_OUTER_VIEW, TAGS.DISPLAY_QUESTION],
            entry: [
              send({ type: RECORDER_EVENTS.START_RECORDING }, { to: (context) => context.recorderRef as ActorRef<DoneInvokeEvent<any>> }),
              questionEventFormatter('STARTED'),
            ],
            on: {
              [EVENTS.STOP_RECORDING]: [
                {
                  actions: [
                    { type: ACTIONS.SENTRY, data: { eventName: 'VIDEO_CORRUPTED' } },
                    ACTIONS.INCREASE_FAILED_RECORDING_ATTEMPTS,
                  ],
                  cond: GUARDS.IS_VIDEO_CORRUPTED,
                },
                {
                  actions: [
                    ACTIONS.STORE_TAKE,
                    ACTIONS.UPDATE_VIDEO_PART,
                  ],
                  target: `#widget.${STATES.PREVIEW}`,
                },
              ],
              [EVENTS.RECORDER_ERROR]: {
                actions: [
                  { type: ACTIONS.SENTRY, data: { eventName: recorderErrors.NoSoundError.name } },
                  { type: ACTIONS.SET_CHECKS, data: { key: 'microphone', state: MICROPHONE_STATES.ERROR } as IChecksMetaObj },
                ],
                cond: GUARDS.IS_NO_SOUND_ERROR,
              },
              [EVENTS.RETRY]: {
                actions: [
                  ACTIONS.RECORDER_RE_INIT_AUTO_START,
                  ACTIONS.CLEAR_VIDEO_ERROR,
                ],
              },
            },
          },
        },
      },
      [STATES.PREVIEW]: {
        tags: [TAGS.DISPLAY_QUESTION],
        entry: [ACTIONS.SPAWN_PREVIEW],
        exit: [ACTIONS.STOP_PREVIEW],
        on: {
          [EVENTS.QUESTION_MODE]: {
            actions: [
              pure(({ currentQuestion, questions }) => ({
                type: ACTIONS.EMIT_TRACKING_EVENT,
                data: {
                  eventType: currentQuestion === questions.length ? EVENT_TYPES.PRACTICE_FINISHED : EVENT_TYPES.PRACTICE_SKIPPED,
                  ...(currentQuestion !== questions.length && { extraData: {
                    questionNumber: currentQuestion,
                    numberOfQuestions: questions.length,
                  },
                  }) } as ITrackEvent,
              })),
              ACTIONS.SET_QUESTION_MODE,
              ACTIONS.SET_NEXT_QUESTION,
              { type: ACTIONS.CONSOLE_DEBUG, data: { message: DEBUG.INTERVIEW_STARTED } },
              { type: ACTIONS.EMIT_TRACKING_EVENT, data: { eventType: EVENT_TYPES.START_CLICKED } as ITrackEvent },
            ],
            target: STATES.NEXT_QUESTION_MIDDLEWARE,
            cond: GUARDS.IS_PRACTICE_MODE,
          },
          [EVENTS.GET_TAKES]: {
            actions: [ACTIONS.GET_TAKES],
          },
          [EVENTS.TAKES_RECEIVED]: {
            actions: [ACTIONS.SEND_TAKES],
          },
          [EVENTS.TAKE_TO_UPLOAD_RECEIVED]: [
            {
              actions: [
                ACTIONS.UPDATE_VIDEO_FILE_COMPLETED,
                ACTIONS.SPAWN_UPLOADER,
                ACTIONS.UPDATE_TOTAL_FILES_SIZE,
                ACTIONS.UPDATE_VIDEO_OBJECT,
                { type: ACTIONS.SESSION_EVENT, data: { event: 'widget_submitted', type: 'date' } },
              ],
              target: STATES.UPLOADING,
              cond: GUARDS.IS_LAST_INTERVIEW_QUESTION,
            },
            {
              actions: [
                ACTIONS.UPDATE_VIDEO_FILE_COMPLETED,
                ACTIONS.SPAWN_UPLOADER,
                ACTIONS.UPDATE_TOTAL_FILES_SIZE,
                ACTIONS.RESET_FAILED_RECORDING_ATTEMPTS,
              ],
              target: STATES.UPLOADING,
              cond: GUARDS.IS_SAFE_UPLOAD,
            },
            {
              actions: [
                ACTIONS.UPDATE_VIDEO_FILE_COMPLETED,
                ACTIONS.SPAWN_UPLOADER,
                ACTIONS.UPDATE_TOTAL_FILES_SIZE,
                ACTIONS.UPDATE_QUESTION_NUMBER,
                ACTIONS.RESET_FAILED_RECORDING_ATTEMPTS,
              ],
              target: STATES.NEXT_QUESTION_MIDDLEWARE,
            },
          ],
          [EVENTS.NEXT_RETAKE]: {
            actions: [
              ACTIONS.UPDATE_RETAKES_NUMBER,
              { type: ACTIONS.SESSION_EVENT, data: { event: 'widget_retake_count', type: 'number' } },
              { type: ACTIONS.CONSOLE_DEBUG, data: { message: DEBUG.RETAKE_CLICKED } },
              { type: ACTIONS.EMIT_TRACKING_EVENT, data: { eventType: EVENT_TYPES.RETAKE_CLICKED } as ITrackEvent },
            ],
            target: `${STATES.RE_INIT_RECORDER}.${STATES.RE_INIT_RECORDER__NEXT_RETAKE}`,
            cond: GUARDS.IS_NOT_LAST_TAKE,
          },
          [EVENTS.NEXT_QUESTION]: [
            {
              actions: [
                ACTIONS.GET_TAKE,
                { type: ACTIONS.CONSOLE_DEBUG, data: { message: DEBUG.QUESTION_SUBMITTED } },
              ],
              cond: GUARDS.IS_QUESTION_MODE,
            },
            {
              actions: [
                ACTIONS.UPDATE_QUESTION_NUMBER,
                ACTIONS.RESET_FAILED_RECORDING_ATTEMPTS,
              ],
              target: STATES.NEXT_QUESTION_MIDDLEWARE,
            },
          ],
        },
      },
      [STATES.TIMES_UP]: {
        on: {
          [EVENTS.NEXT_STEP]: STATES.NEXT_QUESTION_MIDDLEWARE,
        },
      },
      [STATES.NEXT_QUESTION_MIDDLEWARE]: {
        always: [
          {
            actions: [ACTIONS.DEACTIVATE_RECORDER],
            target: `#widget.${STATES.INTERVIEW}`, // If it's an assessment - we dont need to init the recorder
            cond: GUARDS.IS_ASSESSMENT_QUESTION,
          },
          {
            target: `#widget.${STATES.INTERVIEW}`, // If the recorder is ready - we dont need to re-init
            cond: GUARDS.IS_RECORDER_READY,
          },
          {
            target: `${STATES.RE_INIT_RECORDER}.${STATES.RE_INIT_RECORDER__NEXT_QUESTION}`,
          },
        ],
      },
      [STATES.RE_INIT_RECORDER]: {
        entry: [ACTIONS.RECORDER_RE_INIT],
        states: {
          [STATES.RE_INIT_RECORDER__NEXT_RETAKE]: {
            on: {
              [EVENTS.READY_TO_START_RECORDING]: {
                target: `#widget.${STATES.INTERVIEW}.${STATES.INTERVIEW__RECORDING}`,
              },
            },
          },
          [STATES.RE_INIT_RECORDER__NEXT_QUESTION]: {
            on: {
              [EVENTS.READY_TO_START_RECORDING]: {
                target: `#widget.${STATES.INTERVIEW}`,
              },
            },
          },
        },
      },
      [STATES.UPLOADING]: {
        tags: [TAGS.DISPLAY_OUTER_VIEW, TAGS.DISPLAY_UPLOAD],
        initial: STATES.UPLOADING__SELECTOR,
        states: {
          [STATES.UPLOADING__SELECTOR]: {
            entry: [
              { type: ACTIONS.CONSOLE_DEBUG, data: { message: DEBUG.UPLOADING_STATE } },

            ],
            always: [
              { target: STATES.UPLOADING__LAST_QUESTION, cond: GUARDS.IS_LAST_INTERVIEW_QUESTION },
              { target: STATES.UPLOADING__SAFE_UPLOAD },
            ],
          },
          [STATES.UPLOADING__LAST_QUESTION]: {
            entry: [
              { type: ACTIONS.SESSION_EVENT, data: { event: 'widget_completed', type: 'date' } },
            ],
            always: [
              { target: `#widget.${STATES.CONFIRM}`, cond: GUARDS.ARE_ALL_QUESTIONS_UPLOADED },
            ],
          },
          [STATES.UPLOADING__SAFE_UPLOAD]: {
            always: [
              { target: `#widget.${STATES.NEXT_QUESTION_MIDDLEWARE}`, cond: GUARDS.IS_CURRENT_QUESTION_SAFE_UPLOADED },
            ],
            exit: [ACTIONS.UPDATE_QUESTION_NUMBER],
          },
        },
        invoke: {
          id: 'updateVideo',
          src: SERVICES.UPDATE_VIDEO_OBJECT_CALL,
          onError: {
            actions: [() => console.error('UPDATE_VIDEO_COMPLETED_ERROR:'), console.error, ACTIONS.SENTRY],
          },
        },
      },
      [STATES.CONFIRM_WATING]: {
        tags: [TAGS.DISPLAY_OUTER_VIEW, TAGS.DISPLAY_UPLOAD],
        always: {
          target: STATES.CONFIRM,
          cond: GUARDS.IS_CONNECTED,
        },
      },
      [STATES.CONFIRM]: {
        tags: [TAGS.DISPLAY_OUTER_VIEW, TAGS.DISPLAY_UPLOAD],
        invoke: {
          id: 'getVideo',
          src: (context) => getVideo(context.widgetConfig.video?.video_id || ''),
          onDone: [
            {
              target: STATES.FINISHED,
              cond: (_, event) => !!event.data.data.data.video.uploaded,
            },
            {
              actions: [
                ACTIONS.UPDATE_UPLOADED_FALSE_COUNT,
                { type: ACTIONS.SENTRY, data: { eventName: 'CONFIRM::UPLOADED_FALSE' } },
              ],
              target: STATES.CONFIRM_WATING,
              cond: GUARDS.SHOULD_TRY_TO_CONFIRM,
            },
            {
              actions: [
                { type: ACTIONS.SET_VIDEO_ERROR, data: { errorMessage: 'Error, Please contact support' } },
                { type: ACTIONS.EMIT_TRACKING_EVENT, data: { eventType: EVENT_TYPES.CONFIRM_UPLOADED_FAILED } as ITrackEvent },
              ],
              target: STATES.ERROR,
            },
          ],
          onError: [
            {
              actions: [() => console.error('UPDATE_VIDEO_UPLADED_ERROR:'), console.error, { type: ACTIONS.SENTRY, data: { eventName: 'CONFIRM::ERROR__NOT_FOUND' } }],
              target: STATES.FINISHED,
              cond: (_, event) => event.data.response.status === STATUS_CODES.NOT_FOUND,
            },
            {
              actions: [() => console.error('UPDATE_VIDEO_UPLADED_ERROR:'), console.error, { type: ACTIONS.SENTRY, data: { eventName: 'CONFIRM::ERROR' } }],
              target: STATES.CONFIRM_WATING,
            },
          ],
        },
      },
      [STATES.FINISHED]: {
        entry: [
          { type: ACTIONS.CONSOLE_DEBUG, data: { message: DEBUG.INTERVIEW_SUBMITTED } },
          { type: ACTIONS.EMIT_TRACKING_EVENT, data: { eventType: EVENT_TYPES.FINISHED } as ITrackEvent },
        ],
        type: 'final',
      },
      [STATES.ERROR]: {
        on: {
          [EVENTS.RETRY]: {
            target: STATES.INIT,
          },
        },
      },
    },
  },
  {
    actions: {
      [ACTIONS.SESSION_EVENT]: (context, event, meta) => {
        triggerEvent[meta.action.data.type as keyof typeof triggerEvent]?.(meta.action.data.event as never);
      },
      [ACTIONS.SET_SESSION]: (context, event) => {
        updateSession({
          candidate_id: context.widgetConfig.candidate?.company_id,
          video_id: context.widgetConfig.candidate?.video_id,
          email: context.widgetConfig.candidate?.email,
          widget_version: '3.0.0',
          company_id: context.widgetConfig.job?.company_id,
          apiKey: context.widgetConfig.job?.apiKey,
          widget_shown: new Date(),
        }, configFile(context.widgetConfig.config.env).serverUrl).then(() => {});
      },
      [ACTIONS.SET_EVENT_ID]: ({ widgetConfig }) => {
        updateEventId(widgetConfig.event_id);
      },
      [ACTIONS.EMIT_TRACKING_EVENT]: (_, _event, meta) => {
        emitTrackEvent(meta.action.data as ITrackEvent);
      },
      [ACTIONS.SENTRY]: (context, event, meta) => SentryTrackingFn(event, context, meta.action.data?.eventName || 'accWidgetMachine', 'fatal'),
      [ACTIONS.CONSOLE_LOG]: (context, event, meta) => console.log(
        '%c [WidgetMachine]',
        CONSOLE_LOG_STYLE,
        context,
        event,
        meta,
      ),
      [ACTIONS.CONSOLE_DEBUG]: (context, event, meta) => context.widgetConfig.config.debug && console.debug(`%c[DEBUG] ${meta.action.data?.message || ''}`, CONSOLE_LOG_STYLE, event.data),
      [ACTIONS.UPDATE_AXION_AUTH]: (context) => updateVideoAxiosAuth(context.widgetConfig?.config.auth || ''),
      [ACTIONS.SET_CONNECTION]: assign((_, event) => ({
        isConnected: event.data,
      })),
      [ACTIONS.SET_CONFIG]: assign((context, event) => ({
        widgetConfig: { ...context.widgetConfig, ...event.data.data.data as IWidgetConfig, config: Object.assign(context.widgetConfig.config, event.data.data.data.widget_config as IWidgetConfig['config']) },
        isResumed: (event.data.data.data as IWidgetConfig).video?.videos?.some((v) => v.started),
      })),
      [ACTIONS.SET_NEXT_QUESTION]: assign((context, event) => {
        const videos = (context.widgetConfig || (event.data.data.data as IWidgetConfig)).video?.videos || [];
        const nonUploadedQuestoinIndex = videos.findIndex((video) => !video.uploaded);
        const currentQuestion = nonUploadedQuestoinIndex !== -1 ? nonUploadedQuestoinIndex + 1 : videos.length;
        return {
          currentQuestion,
          timer: context.questions[currentQuestion - 1]?.partDuration ?? 0,
        };
      }),
      [ACTIONS.SET_SPEED_TEST]: assign((_, event) => ({
        speedTestResult: event.data,
      })),
      [ACTIONS.UPDATE_RECORDER_SPEED_TEST]: send(
        (_, { data }) => ({ type: RECORDER_EVENTS.SPEED_TEST_RESULT_UPDATED, data: { speedTestLevel: data >= FAST_UPLOAD_SPEED ? SPEED_TEST_LEVEL.HIGH : data > SLOW_UPLOAD_SPEED ? SPEED_TEST_LEVEL.MEDIUM : SPEED_TEST_LEVEL.LOW } }),
        { to: ({ recorderRef }) => recorderRef as ActorRef<any, any> },
      ),
      [ACTIONS.SET_CHECKS]: assign(({ checksState, checksMessage, speedTestResult }, event, meta) => {
        const { key, state, message } = (meta.action as any).data as IChecksMetaObj;
        const stateToSet = state || (speedTestResult >= FAST_UPLOAD_SPEED ? INTERNET_STATES.CONNECTED : INTERNET_STATES.SLOW_CONNECTION);
        const messageToSet = key !== 'internet'
          ? message || event.data.message
          : message || (speedTestResult >= FAST_UPLOAD_SPEED ? INTERNET_STATES_MESSAGES.CONNECTED : INTERNET_STATES_MESSAGES.SLOW_CONNECTION);
        return {
          checksState: {
            ...checksState,
            [key]: stateToSet,
          },
          checksMessage: {
            ...checksMessage,
            [key]: messageToSet,
          },
        };
      }),
      [ACTIONS.SET_PRACTICE_MODE]: assign(({ widgetConfig }) => ({
        recordingType: TAKE_TYPES.PRACTICE,
        questions: widgetConfig.config.practiceQuestions?.length ? widgetConfig.config.practiceQuestions : i18n.t('practice.questions', { returnObjects: true }),
      })),
      [ACTIONS.SET_QUESTION_MODE]: assign((context) => ({
        recordingType: TAKE_TYPES.QUESTION,
        questions: context.widgetConfig?.job?.questions || [],
        currentQuestion: 1,
        currentTake: 1,
      })),
      [ACTIONS.SPAWN_NETWORK]: assign((_) => ({
        networkRef: spawn(networkMachine, { name: 'network' }),
      })),
      [ACTIONS.SPAWN_STORAGE]: assign((_) => ({
        storageRef: spawn(storageMachine, { name: 'storage' }),
      })),
      [ACTIONS.SPAWN_RECORDER]: assign((context) => ({
        recorderRef: spawn(
          recorderMachineV2.withContext({
            ...recorderMachineV2.context,
            videoRef: context.videoRef,
            speedTestLevel: SPEED_TEST_LEVEL.HIGH as any,
            recordWithoutVideo: context.widgetConfig.config.recordWithoutVideo,
            forcedView: context.widgetConfig.config.forcedView,
          }),
          { name: 'recorder' },
        ),
      })),
      [ACTIONS.SPAWN_PREVIEW]: assign((_) => ({
        previewRef: spawn(previewMachine, { name: 'preview' }),
      })),
      [ACTIONS.STOP_RECORDER]: assign((context) => {
        (context.recorderRef as any).stop();
        return {
          recorderRef: null,
        };
      }),
      [ACTIONS.STOP_PREVIEW]: assign((context) => {
        (context.previewRef as any).stop();
        return {
          previewRef: null,
        };
      }),
      [ACTIONS.SPAWN_UPLOADER]: assign(({
        widgetConfig, uploaderRefs, isConnected, currentQuestion, currentTake, videoDimensions,
      }, event) => ({
        uploaderRefs: {
          ...uploaderRefs,
          [getUploaderName(currentQuestion)]: spawn(
            accUploaderMachine.withContext({
              ...accUploaderMachine.context,
              isConnected,
              currentQuestion,
              file: event.data.file || null,
              videoId: widgetConfig?.video?.video_id || '',
              videoFile: {
                ...accUploaderMachine.context.videoFile,
                ...widgetConfig?.video?.videos?.[currentQuestion - 1],
                retakes: currentTake,
                selectedTake: event.data.selectedTake || 0,
                ...(event.data.answer && { answer: event.data.answer }),
                ...(event.data.videoLength && { videoLength: event.data.videoLength }),
                originalDimensions: videoDimensions,
              } as ALL_VIDEO_FILE_TYPES,
            }),
            { name: getUploaderName(currentQuestion), autoForward: true },
          ),
        },
      })),
      [ACTIONS.SPAWN_COUNT_DOWN]: assign({
        countDownRef: ({ questions, currentQuestion }, event, meta) => spawn(counterMachine.withContext({
          ...counterMachine.context,
          callback: (meta.action as any).data.callback,
          isAutoStart: !!questions[currentQuestion - 1].partDuration,
        }), { name: 'countDownRef' }),
      }),
      [ACTIONS.STOP_COUNT_DOWN]: assign((context) => {
        (context.countDownRef as any).stop();
        return {
          countDownRef: null,
        };
      }),
      [ACTIONS.STORE_TAKE]: send(
        (context, event) => ({
          type: STORAGE_EVENTS.STORE_TAKE,
          data: {
            takeType: context.recordingType,
            currentQuestion: context.currentQuestion,
            take: event.data.webm,
            videoLength: Math.round((event.data.videoLength || 0) / 1000), // convert videoLength from miliseconds to seconds
          },
        }),
        { to: (context) => context.storageRef as ActorRef<DoneInvokeEvent<any>> },
      ),
      [ACTIONS.GET_TAKE]: send(
        ({ currentQuestion }, event) => ({
          type: STORAGE_EVENTS.GET_TAKE,
          data: { currentQuestion, selectedTake: event.data },
        }),
        { to: (context) => context.storageRef as ActorRef<DoneInvokeEvent<any>> },
      ),
      [ACTIONS.GET_TAKES]: send(
        (context) => ({
          type: STORAGE_EVENTS.GET_TAKES,
          data: { takeType: context.recordingType, currentQuestion: context.currentQuestion },
        }),
        { to: (context) => context.storageRef as ActorRef<DoneInvokeEvent<any>> },
      ),
      [ACTIONS.SEND_TAKES]: send(
        (context, event) => ({
          type: PREVIEW_EVENTS.TAKES_RECEIVED,
          data: event.data,
        }),
        { to: (context) => context.previewRef as ActorRef<DoneInvokeEvent<any>> },
      ),
      [ACTIONS.UPDATE_VIDEO_FILE_COMPLETED]: assign(({ widgetConfig, currentQuestion }) => ({
        widgetConfig: {
          ...widgetConfig,
          video: {
            ...widgetConfig?.video,
            videos: widgetConfig?.video?.videos?.map((video, idx) => (idx !== currentQuestion - 1 ? video : ({ ...video, completed: true }))),
          },
        } as IWidgetConfig,
      })),
      [ACTIONS.UPDATE_TOTAL_FILES_SIZE]: assign(({ totalFileSize }, event) => ({
        totalFileSize: totalFileSize + (event.data.file.size || 0),
      })),
      [ACTIONS.UPDATE_TOTAL_UPLOADED_FILES_SIZE]: assign(({ totalUploadedFilesSize, totalUploadSpeed }, event) => ({
        totalUploadedFilesSize: { ...totalUploadedFilesSize, [event.data.questionNumber]: event.data.progressEvent.loaded },
        totalUploadSpeed: { ...totalUploadSpeed, [event.data.questionNumber]: event.data.uploadSpeed },
      })),
      [ACTIONS.UPDATE_QUESTION_NUMBER]: assign(({
        currentQuestion, questions, widgetConfig, recordingType,
      }) => {
        const questionNumberIdx = recordingType === TAKE_TYPES.QUESTION
          ? widgetConfig.video?.videos?.findIndex((video, idx) => !video.uploaded && idx > currentQuestion - 1) ?? questions.length
          : Math.min(currentQuestion + 1, questions.length);
        const currentQuestionNumber = Math.min(questionNumberIdx + 1, questions.length || 0);
        return {
          currentQuestion: currentQuestionNumber,
          currentTake: 1,
          timer: questions[currentQuestionNumber - 1]?.partDuration ?? 0,
        };
      }),
      [ACTIONS.UPDATE_RETAKES_NUMBER]: assign(({ currentTake, currentQuestion, questions }) => ({ // Number of retakes is number of takes (we will need to update this field everywhere at some point)
        currentTake: Math.min(currentTake + 1, questions[currentQuestion - 1].numOfRetakes || 1),
      })),
      [ACTIONS.UPDATE_TIMER]: assign(({ timer }) => ({ timer: Math.max(timer - 1, 0) })),
      [ACTIONS.RECORDER_RE_INIT]: send(
        (context) => ({ type: RECORDER_EVENTS.RE_INIT, data: { isAutoStart: false } }),
        { to: ({ recorderRef }) => recorderRef as ActorRef<any, any> },
      ),
      [ACTIONS.DEACTIVATE_RECORDER]: send(
        (context) => ({ type: RECORDER_EVENTS.SEND_RECORDER_TO_WAITING_STATE }),
        { to: ({ recorderRef }) => recorderRef as ActorRef<any, any> },
      ),
      [ACTIONS.RECORDER_RE_INIT_AUTO_START]: send(
        (context) => ({ type: RECORDER_EVENTS.RE_INIT, data: { isAutoStart: true } }),
        { to: ({ recorderRef }) => recorderRef as ActorRef<any, any> },
      ),
      [ACTIONS.UPDATE_RECORDER_PARAMS]: send(
        ({ questions, currentQuestion, currentTake }) => ({
          type: RECORDER_EVENTS.SET_QUESTION_PARAMS,
          data: {
            countdown: currentTake === 1 ? questions[currentQuestion - 1].thinkingTime || DEFAULT_COUNT_DOWN : DEFAULT_COUNT_DOWN,
            duration: questions[currentQuestion - 1].partDuration || DEFAULT_DURATION,
          },
        }),
        { to: (context) => context.recorderRef as ActorRef<DoneInvokeEvent<any>> },
      ),
      [ACTIONS.UPDATE_TOTAL_UPLOADED]: assign(({ widgetConfig }, { data: { questionNumber, videoFile } }) => ({
        widgetConfig: {
          ...widgetConfig,
          video: {
            ...widgetConfig.video,
            videos: widgetConfig.video?.videos?.map((video, idx) => ((idx !== questionNumber - 1) ? video : videoFile)),
          },
        } as IWidgetConfig,
      })),
      [ACTIONS.SET_VIDEO_ERROR]: assign((_, event, meta) => ({
        error: event.data?.response?.data || { errorMessage: event.data?.message || (meta.action as any).data?.errorMessage },
      })),
      [ACTIONS.REVOKE_MEMORY]: send(
        (_, event) => ({ type: STORAGE_EVENTS.REMOVE_TAKES, data: { questionToRemove: event.data.questionNumber } }),
        { to: ({ storageRef }) => storageRef as ActorRef<DoneInvokeEvent<any>> },
      ),
      [ACTIONS.UPDATE_VIDEO_OBJECT]: assign(({ widgetConfig, recordingType }) => ({
        widgetConfig: {
          ...widgetConfig,
          video: {
            ...widgetConfig.video,
            started: recordingType === TAKE_TYPES.QUESTION,
            completed: !!widgetConfig.video?.videos?.every((v) => v.completed),
          },
        } as IWidgetConfig,
      })),
      [ACTIONS.UPDATE_VIDEO_PART]: assign(({ widgetConfig, recordingType, currentQuestion }, event) => (
        recordingType === TAKE_TYPES.QUESTION ? {
          widgetConfig: {
            ...widgetConfig,
            video: {
              ...widgetConfig.video,
              videos: (widgetConfig.video?.videos || []).map((v, idx) => ({
                ...v,
                started: v.started || currentQuestion === idx + 1,
                completed: v.completed || currentQuestion > idx + 1 || (currentQuestion === idx + 1 && event.type === EVENTS.STOP_RECORDING),
              })),
            },
          } as IWidgetConfig,
        } : {})),
      [ACTIONS.INCREASE_FAILED_RECORDING_ATTEMPTS]: assign(({ failedRecordingAttempts }) => {
        const failedRecordingCount = Math.min(failedRecordingAttempts + 1, MAX_FAILED_RECORDING_ATTEMPTS);
        return {
          failedRecordingAttempts: failedRecordingCount,
          failedRecordingMessage: failedRecordingCount < MAX_FAILED_RECORDING_ATTEMPTS ? VIDEO_CORRUPTED_STATES.RETRY : VIDEO_CORRUPTED_STATES.REDIRECT,
        };
      }),
      [ACTIONS.RESET_FAILED_RECORDING_ATTEMPTS]: assign((_) => ({
        failedRecordingAttempts: 0,
        failedRecordingMessage: VIDEO_CORRUPTED_STATES.NO_ERROR,
      })),
      [ACTIONS.CLEAR_VIDEO_ERROR]: assign((_) => ({
        failedRecordingMessage: VIDEO_CORRUPTED_STATES.NO_ERROR,
      })),
      [ACTIONS.UPDATE_VIDEO_DIMENSIONS]: assign((_, event) => ({
        videoDimensions: {
          width: event.data.width,
          height: event.data.height,
        },
      })),
      [ACTIONS.UPDATE_UPLOADED_FALSE_COUNT]: assign(({ confirmUploadedFalseCount }) => ({
        confirmUploadedFalseCount: confirmUploadedFalseCount + 1,
      })),
    },
    services: {
      [SERVICES.UPDATE_VIDEO_OBJECT_CALL]: ({ widgetConfig: { video }, recordingType, speedTestResult }, event, meta) => (callback, onReceive) => {
        if (recordingType === TAKE_TYPES.PRACTICE && !meta.data.force) return Promise.resolve();
        return updateVideo({ ...video, bandwidth: `${speedTestResult} MB/s` } as IVideo);
      },
      [SERVICES.UPDATE_VIDEO_PART_CALL]: ({ widgetConfig: { video }, recordingType, currentQuestion, videoDimensions }) => (callback, onReceive) => {
        if (recordingType === TAKE_TYPES.PRACTICE) return Promise.resolve();
        return updateVideoFile(video?.video_id || '', { ...video?.videos?.[currentQuestion - 1], originalDimensions: videoDimensions } as ALL_VIDEO_FILE_TYPES);
      },
    },
    guards: {
      [GUARDS.IS_VIDEO_RECORDING_SKIP]: ({ widgetConfig }) => !!widgetConfig.config.recordWithoutVideo,
      [GUARDS.NO_STORAGE]: ({ storageRef }) => !storageRef,
      [GUARDS.NO_RECORDER]: ({ recorderRef }) => !recorderRef,
      [GUARDS.IS_THINKING_TIME]: ({ questions, currentQuestion }) => {
        const currentQuestionObj = questions[currentQuestion - 1];
        return (currentQuestionObj.answerType && currentQuestionObj.answerType !== ANSWER_TYPES.VIDEO)
          ? !!currentQuestionObj.partDuration
          : !!currentQuestionObj.thinkingTime;
      },
      [GUARDS.IS_VIDEO_QUESTION]: ({ questions, currentQuestion }) => !!questions[currentQuestion - 1].videoQuestion,
      [GUARDS.IS_QUESTION_MODE]: ({ recordingType }) => recordingType === TAKE_TYPES.QUESTION,
      [GUARDS.IS_PRACTICE_MODE]: ({ recordingType }) => recordingType === TAKE_TYPES.PRACTICE,
      [GUARDS.IS_NOT_LAST_TAKE]: ({ questions, currentQuestion, currentTake }) => currentTake < questions[currentQuestion - 1].numOfRetakes,
      [GUARDS.IS_LAST_INTERVIEW_QUESTION]: ({ widgetConfig, currentQuestion, recordingType }) => recordingType === TAKE_TYPES.QUESTION && (widgetConfig.video?.videos?.reduce((acc, v, idx) => (!v.uploaded ? idx + 1 : acc), 0) || 0) === currentQuestion,
      [GUARDS.CAN_RETEST_SPEED_CONNECTION]: ({ speedTestResult }) => (speedTestResult || 0) < FAST_UPLOAD_SPEED,
      [GUARDS.IS_CONNECTED]: ({ isConnected }) => isConnected,
      [GUARDS.IS_DISCONNECTED]: ({ isConnected }) => !isConnected,
      [GUARDS.CAN_START_INTERVIEW]: ({ checksState }) => (checksState.camera === CAMERA_STATES.READY || checksState.camera === CAMERA_STATES.SKIP) && checksState.microphone === MICROPHONE_STATES.READY && [INTERNET_STATES.CONNECTED, INTERNET_STATES.SLOW_CONNECTION].includes(checksState.internet as any),
      [GUARDS.ARE_ALL_QUESTIONS_UPLOADED]: ({ widgetConfig }) => !!widgetConfig.video?.videos?.every((video) => video.uploaded),
      [GUARDS.IS_CURRENT_QUESTION_SAFE_UPLOADED]: ({ widgetConfig, currentQuestion }) => !!(widgetConfig.config.useSafeUpload && widgetConfig.video?.videos?.[currentQuestion - 1].uploaded),
      [GUARDS.SHOULD_SHOW_WELCOME]: ({ widgetConfig }) => !!widgetConfig.config.introVideo || !!widgetConfig.config.welcomeTitle || !!widgetConfig.config.welcomeText,
      [GUARDS.IS_NO_SOUND_ERROR]: (_, { data }) => data.code === MICROPHONE_NO_SOUND_ERROR_CODE,
      [GUARDS.IS_VIDEO_CORRUPTED]: (_, { data }) => !data.webm.size,
      [GUARDS.IS_AUDIO_ERROR]: (_, { data }) => data.type === TYPES_EXPLANATION.AUDIO || data.type === TYPES_EXPLANATION.VIDEO_AUDIO,
      [GUARDS.IS_VIDEO_ERROR]: (_, { data }) => data.type === TYPES_EXPLANATION.VIDEO || data.type === TYPES_EXPLANATION.VIDEO_AUDIO,
      [GUARDS.THERE_ARE_NO_VIDEO_QUESTIONS_TYPE]: ({ widgetConfig }) => !!widgetConfig.job?.questions?.every((q) => q.answerType && q.answerType !== ANSWER_TYPES.VIDEO),
      [GUARDS.IS_RECORDER_READY]: ({ recorderRef }) => recorderRef?.getSnapshot().value === RECORDER_STATES.IDLE,
      [GUARDS.IS_ASSESSMENT_QUESTION]: ({ questions, currentQuestion }) => !!questions[currentQuestion - 1].answerType && questions[currentQuestion - 1].answerType !== ANSWER_TYPES.VIDEO,
      [GUARDS.IS_TIMES_UP]: (_, event) => !!event.data.isTimesUp,
      [GUARDS.SHOULD_TRY_TO_CONFIRM]: ({ confirmUploadedFalseCount }) => confirmUploadedFalseCount <= MAX_CONFIRM_ATTEMPTS,
      [GUARDS.IS_SAFE_UPLOAD]: ({ widgetConfig }) => !!widgetConfig.config.useSafeUpload,
    },
  },
);
